import type { LeaderboardScrollGesture } from "../game/input";

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export interface ContinuousLeaderboardScrollConfig {
  kind: "continuous";
  maxOffset: number;
  speedPxPerSec: number;
  idleDelayMs?: number;
}

export interface TargetLeaderboardScrollConfig {
  kind: "target";
  maxOffset: number;
  targetOffset: number;
  speedPxPerSec: number;
  idleDelayMs?: number;
}

export type LeaderboardScrollConfig =
  | ContinuousLeaderboardScrollConfig
  | TargetLeaderboardScrollConfig;

export interface LeaderboardScrollDebugState {
  surfaceKey: string | null;
  offset: number;
  held: boolean;
  lastInteractionMs: number;
}

export class LeaderboardScrollController {
  private surfaceKey: string | null = null;
  private offset = 0;
  private held = false;
  private lastUpdateMs = -1;
  private lastInteractionMs = 0;

  setSurface(surfaceKey: string | null, nowMs: number): void {
    if (this.surfaceKey === surfaceKey) {
      if (this.lastUpdateMs < 0) {
        this.lastUpdateMs = nowMs;
      }
      return;
    }
    this.surfaceKey = surfaceKey;
    this.offset = 0;
    this.held = false;
    this.lastUpdateMs = nowMs;
    this.lastInteractionMs = nowMs;
  }

  resolve(config: LeaderboardScrollConfig, gesture: LeaderboardScrollGesture, nowMs: number): number {
    const maxOffset = Math.max(0, config.maxOffset);
    if (this.lastUpdateMs < 0) {
      this.lastUpdateMs = nowMs;
    }
    const dtSec = Math.max(0, nowMs - this.lastUpdateMs) / 1000;
    this.lastUpdateMs = nowMs;

    this.held = gesture.held;
    if (gesture.lastInteractionMs > 0) {
      this.lastInteractionMs = Math.max(this.lastInteractionMs, gesture.lastInteractionMs);
    }
    if (gesture.deltaY !== 0) {
      this.offset = clamp(this.offset - gesture.deltaY, 0, maxOffset);
    } else {
      this.offset = clamp(this.offset, 0, maxOffset);
    }

    const idleDelayMs = config.idleDelayMs ?? 5000;
    const idleElapsedMs = nowMs - this.lastInteractionMs;
    if (!this.held && idleElapsedMs >= idleDelayMs && maxOffset > 0) {
      if (config.kind === "continuous") {
        this.offset = Math.min(maxOffset, this.offset + config.speedPxPerSec * dtSec);
      } else {
        const targetOffset = clamp(config.targetOffset, 0, maxOffset);
        if (this.offset < targetOffset) {
          this.offset = Math.min(targetOffset, this.offset + config.speedPxPerSec * dtSec);
        }
      }
    }

    this.offset = clamp(this.offset, 0, maxOffset);
    return this.offset;
  }

  getDebugState(): LeaderboardScrollDebugState {
    return {
      surfaceKey: this.surfaceKey,
      offset: this.offset,
      held: this.held,
      lastInteractionMs: this.lastInteractionMs
    };
  }
}
